import React, { useCallback, useRef } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { EmployeeAvatar, MaybeTooltip } from '@modernloop/shared/components';
import { MagicIcon } from '@modernloop/shared/icons';
import { ListItem, ListItemButton, ListSubheader, Stack, Typography } from '@mui/material';
import { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete';

import { EmployeeFragment } from 'src/generated/mloop-graphql';

import FilterSelect, { Props as FilterListProps, FilterListSelectInterface } from 'src/components/FilterSelect';

import useEmployeeId from 'src/hooks/useEmployeeId';

import { Categories, SMART_OPTIONS_INTERVIEWERS_ID, SMART_OPTIONS_SCHEDULER_ID, SmartOptions } from './types';
import useEmployeeSelectData from './useEmployeeSelectData';

type Props = {
  showSmartOptions?: boolean;
  /** Omits the "Interviewers" smart option; used where a single meeting needs a single host. */
  hideInterviewersSmartOption?: boolean;
  interviewerIds?: string[];
  getLabel: FilterListProps<EmployeeFragment>['getLabel'];
  onEmployeeSelect: (employee: EmployeeFragment) => void;
  onSmartOptionSelected?: (type: SmartOptions) => void;
};

const SingleEmployeeSelect = ({
  showSmartOptions,
  hideInterviewersSmartOption,
  interviewerIds,
  getLabel,
  onEmployeeSelect,
  onSmartOptionSelected,
}: Props) => {
  const { t } = useTranslation();
  const currentEmployeeId = useEmployeeId();

  const filterListSelectInterfaceRef = useRef<FilterListSelectInterface | null>(null);

  const { options, loading, error, hasMore, search, handleInputChange, handleScrollToEnd } = useEmployeeSelectData({
    showSmartOptions,
    hideInterviewersSmartOption,
    interviewerIds,
  });

  const handleChange = (event: React.MouseEvent, values: { type: Categories; employee: EmployeeFragment }[]) => {
    if (!values.length || !onEmployeeSelect) {
      return;
    }

    // The nested if is needed to handle the case where onSmartOptionSelected is not defined and the type is SmartOptions
    if (values[0].type === Categories.SmartOptions) {
      if (onSmartOptionSelected) {
        if (values[0].employee.id === SMART_OPTIONS_INTERVIEWERS_ID) {
          onSmartOptionSelected(SmartOptions.Interviewer);
        } else if (values[0].employee.id === SMART_OPTIONS_SCHEDULER_ID) {
          onSmartOptionSelected(SmartOptions.Scheduler);
        }
      }
    } else {
      onEmployeeSelect(values[0].employee);
    }

    filterListSelectInterfaceRef.current?.dismiss();
  };

  const defaultRenderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, values: { type: Categories; employee: EmployeeFragment }) => {
      if (!currentEmployeeId) {
        return null;
      }
      const option = values.employee;

      if (values.type === Categories.SmartOptions) {
        let label = t('hiring-dashboard-common:common.select.interviewer');
        let subtitle = t('hiring-dashboard-common:common.select.interviewerHostSubtitle');

        if (option.id === SMART_OPTIONS_INTERVIEWERS_ID) {
          label = t('hiring-dashboard-common:common.select.interviewers');
          subtitle = t('hiring-dashboard-common:common.select.interviewersSubtitle');
        } else if (option.id === SMART_OPTIONS_SCHEDULER_ID) {
          label = t('hiring-dashboard-common:common.select.schedulerLabel');
          subtitle = '';
        }

        return (
          <ListItem {...props}>
            <ListItemButton>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="nowrap" data-testid={option.id}>
                <MagicIcon color="primary" />
                <Stack>
                  <MaybeTooltip label={label} title={label} />
                  <Typography variant="body2" color="text.secondary">
                    {subtitle}
                  </Typography>
                </Stack>
              </Stack>
            </ListItemButton>
          </ListItem>
        );
      }

      return (
        <ListItem {...props}>
          <ListItemButton>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="nowrap">
              <EmployeeAvatar imageUrl={option.internalProfilePictureUrl} fullName={option.fullName} size={20} />
              <MaybeTooltip
                label={
                  option.id === currentEmployeeId
                    ? t('hiring-dashboard-common:common.select.nameWithYou', { name: option.fullName })
                    : option.fullName || ''
                }
                title={option.fullName || ''}
              />
            </Stack>
          </ListItemButton>
        </ListItem>
      );
    },
    [currentEmployeeId, t]
  );

  const renderGroup = (params: AutocompleteRenderGroupParams) => {
    const group: React.ReactNode[] = [];
    if (params.group) {
      group.push(
        <ListSubheader
          disableSticky
          key={params.key}
          component="div"
          sx={{
            fontWeight: (theme) => theme.typography.fontWeightMedium,
            padding: (theme) => `${theme.spacing(1)} ${theme.spacing(0.5)}`,
          }}
        >
          <Typography variant="body2" color="primary" fontWeight={600}>
            {params.group === Categories.AllEmployees && t('hiring-dashboard-common:common.select.allEmployees')}
            {params.group === Categories.InterviewPanel && t('hiring-dashboard-common:common.select.interviewPanel')}
            {params.group === Categories.SmartOptions && t('hiring-dashboard-common:common.select.smartOptions')}
          </Typography>
        </ListSubheader>
      );
    }
    group.push(params.children);
    return group;
  };

  return (
    <FilterSelect
      placeholderText={t('hiring-dashboard-common:common.select.searchEmployees')}
      filterListSelectInterfaceRef={filterListSelectInterfaceRef}
      options={options}
      loading={loading}
      error={error}
      onChange={handleChange}
      groupBy={(option) => option.type}
      renderGroup={renderGroup}
      getLabel={getLabel}
      getOptionLabel={(option) => option.employee.fullName || ''}
      renderOption={defaultRenderOption}
      onInputChange={(event: React.ChangeEvent<HTMLInputElement>, value) => {
        if (!search && !hasMore) {
          return;
        }
        handleInputChange(value || '');
      }}
      onScrollToEnd={handleScrollToEnd}
      getChildSize={(child: React.ReactNode) => {
        if (!child) {
          return 0;
        }

        const children = (child as React.ReactElement)?.props?.children
          ? React.Children.toArray((child as React.ReactElement)?.props.children)
          : undefined;

        if (
          children &&
          children.length === 1 &&
          React.isValidElement(children[0]) &&
          children[0].props &&
          children[0].props.children &&
          children[0].props.children.props
        ) {
          if (children[0].props.children.props['data-testid'] === SMART_OPTIONS_INTERVIEWERS_ID) {
            return 66;
          }
          if (children[0].props.children.props['data-testid'] === SMART_OPTIONS_SCHEDULER_ID) {
            return 40;
          }
        }

        return 32;
      }}
    />
  );
};

export default SingleEmployeeSelect;
